// =================================================================
// BLOKAS 1: IMPORTAI IR INTERFACE'AI
// =================================================================

// SUB-BLOKAS 1.1: React ir kitų bibliotekų importai
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Info, AlertCircle, ChevronRight, ChevronDown, X, Settings, SlidersHorizontal
} from 'lucide-react';
import { TimeInterval } from './TimeInterval';
import { cn } from '../../utils/cn';
import { toast } from 'sonner';
import { Tooltip } from '../../ui/Tooltip';
import { buildPatchFromChange } from '../../utils/patchMap';
import { useAuth } from '../../contexts/AuthContext';

console.log(`🔧 [BLOKAS 1.1 SecondaryTime.tsx] React ir bibliotekų importai sėkmingai įkelti`);

// SUB-BLOKAS 1.2: Interface'ų apibrėžimai
interface SecondaryTimeProps {
  botId?: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  selectedInterval: string;
  onIntervalChange: (interval: string) => void;
  primaryInterval: string;
  initialIndicators: any[];
  onIndicatorsChange: (indicators: any[]) => void;
}

interface Indicator {
  id: string;
  type: string;
  settings: Record<string, any>;
  label?: string;
}

console.log(`🔧 [BLOKAS 1.2 SecondaryTime.tsx] Interface'ų apibrėžimai inicijuoti`);

// =================================================================
// BLOKAS 2: PAGRINDINĖS FUNKCIJOS IR KONSTANTOS
// =================================================================

// SUB-BLOKAS 2.1: Helper funkcijos
function humanize(key: string) {
  console.log(`🔧 [BLOKAS 2.1 SecondaryTime.tsx] humanize() funkcija: konvertuoja ${key}`);
  const s = key.replace(/[_-]+/g, ' ').trim();
  if (!s) return 'Indicator';
  return s
    .split(' ')
    .map(w => (w.length <= 3 ? w.toUpperCase() : w.toUpperCase() + w.slice(1)))
    .join(' ');
}

function normalizeIndicators(input: any): Indicator[] {
  console.log(`🔧 [BLOKAS 2.1 SecondaryTime.tsx] normalizeIndicators() funkcija: apdorojama input`, input);
  
  if (Array.isArray(input)) {
    const result = input.map((it: any, idx: number) => ({
      id: it?.id ?? crypto.randomUUID(),
      type: String(it?.type ?? it?.key ?? 'custom'),
      label: it?.label ?? humanize(String(it?.type ?? it?.key ?? 'custom')),
      settings: it?.settings ?? it?.params ?? {},
    }));
    console.log(`🔧 [BLOKAS 2.1 SecondaryTime.tsx] normalizeIndicators(): grąžinamas masyvas su ${result.length} indikatoriais`);
    return result;
  }
  
  if (input && Array.isArray(input.types)) {
    const result = input.types.map((t: any, idx: number) => ({
      id: crypto.randomUUID(),
      type: String(t),
      label: humanize(String(t)),
      settings: {},
    }));
    console.log(`🔧 [BLOKAS 2.1 SecondaryTime.tsx] normalizeIndicators(): grąžinamas masyvas su ${result.length} indikatoriais iš types`);
    return result;
  }
  
  console.log(`🔧 [BLOKAS 2.1 SecondaryTime.tsx] normalizeIndicators(): grąžinamas tuščias masyvas`);
  return [];
}

console.log(`🔧 [BLOKAS 2.1 SecondaryTime.tsx] Helper funkcijos apibrėžtos`);

// SUB-BLOKAS 2.2: Konstanta - prieinami indikatoriai
const availableIndicators = [
  { value: 'rsi', label: 'RSI (Relative Strength Index)', defaultSettings: { period: 14, overbought: 70, oversold: 30 } },
  { value: 'macd', label: 'MACD', defaultSettings: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
  { value: 'bollinger', label: 'Bollinger Bands', defaultSettings: { period: 20, stdDev: 2 } },
  { value: 'sma', label: 'Simple Moving Average', defaultSettings: { period: 20 } },
  { value: 'ema', label: 'Exponential Moving Average', defaultSettings: { period: 20 } },
  { value: 'adx', label: 'ADX (Average Directional Index)', defaultSettings: { period: 14 } },
  { value: 'volume', label: 'Volume', defaultSettings: { period: 20 } },
  { value: 'momentum', label: 'Momentum Oscillator', defaultSettings: { period: 10 } },
  
  // Konceptualūs indikatoriai (be skaitinių nustatymų)
  { value: 'price_action', label: 'Price Action', defaultSettings: {} },
  { value: 'level2_data', label: 'Level 2 Data', defaultSettings: {} },
  { value: 'fibonacci', label: 'Fibonacci Levels/Retracements', defaultSettings: {} },
  { value: 'pivot_points', label: 'Pivot Points', defaultSettings: {} },
  { value: 'candlestick', label: 'Candlestick Patterns', defaultSettings: {} },
  { value: 'support_resistance', label: 'Support/Resistance', defaultSettings: {} },
  { value: 'trend_lines', label: 'Trend Lines', defaultSettings: {} },
  { value: 'volume_profile', label: 'Volume Profile', defaultSettings: {} },
  { value: 'order_flow', label: 'Order Flow', defaultSettings: {} },
  { value: 'market_profile', label: 'Market Profile', defaultSettings: {} },
  { value: 'time_sales', label: 'Time and Sales', defaultSettings: {} }
];

console.log(`🔧 [BLOKAS 2.2 SecondaryTime.tsx] Apibrėžti ${availableIndicators.length} prieinami indikatoriai`);

// SUB-BLOKAS 2.3: Intervalų funkcijos
const intervals = ['1m', '3m', '5m', '15m', '30m', '1h', '4h', '8h', '1d', '1w', '1M'];

// Helper funkcija intervalo indeksui gauti
const getIntervalIndex = (interval: string): number => {
  console.log(`🔧 [BLOKAS 2.3 SecondaryTime.tsx] getIntervalIndex(): ieškomas intervalas ${interval}`);
  const index = intervals.indexOf(interval);
  console.log(`🔧 [BLOKAS 2.3 SecondaryTime.tsx] getIntervalIndex(): intervalo ${interval} indeksas yra ${index}`);
  return index;
};

// Helper funkcija leidžiamiems antriniams intervalams gauti pagal pirminį
const getAllowedSecondaryIntervals = (primaryInterval: string): string[] => {
  console.log(`🔧 [BLOKAS 2.3 SecondaryTime.tsx] getAllowedSecondaryIntervals(): pirminis intervalas ${primaryInterval}`);
  const primaryIndex = getIntervalIndex(primaryInterval);
  
  if (primaryIndex === -1) {
    console.log(`🔧 [BLOKAS 2.3 SecondaryTime.tsx] getAllowedSecondaryIntervals(): pirminis intervalas nerastas, grąžinami visi intervalai`);
    return intervals.slice(1); // Jei pirminis nerastas, leidžiami visi išskyrus pirmą
  }
  
  // Leidžiami tik intervalai, kurie didesni už pirminį
  const allowedIntervals = intervals.slice(primaryIndex + 1);
  console.log(`🔧 [BLOKAS 2.3 SecondaryTime.tsx] getAllowedSecondaryIntervals(): leidžiami intervalai: ${allowedIntervals.join(', ')}`);
  return allowedIntervals;
};

console.log(`🔧 [BLOKAS 2.3 SecondaryTime.tsx] Intervalų funkcijos apibrėžtos`);

// =================================================================
// BLOKAS 3: KOMPONENTO APIBRĖŽIMAS IR BŪSENA
// =================================================================

export const SecondaryTime: React.FC<SecondaryTimeProps> = ({
  botId,
  enabled,
  onEnabledChange,
  selectedInterval,
  onIntervalChange,
  primaryInterval,
  initialIndicators,
  onIndicatorsChange
}) => {
  console.log(`🔧 [BLOKAS 3 SecondaryTime.tsx] KOMPONENTAS INIT: inicijuojamas SecondaryTime komponentas`, {
    botId,
    enabled,
    selectedInterval,
    primaryInterval,
    initialIndicatorsCount: initialIndicators?.length || 0
  });

  // SUB-BLOKAS 3.1: State kintamieji
  const { user } = useAuth();
  const [editingIndicator, setEditingIndicator] = useState<string | null>(null);
  const [indicators, setIndicators] = useState<Indicator[]>(normalizeIndicators(initialIndicators));
  const prevBotIdRef = React.useRef(botId);

  console.log(`🔧 [BLOKAS 3.1 SecondaryTime.tsx] State kintamieji inicijuoti:`, {
    editingIndicator,
    indicatorsCount: indicators.length,
    userId: user?.id
  });

  console.log('🟣 [BLOKAS 3.1 SecondaryTime.tsx] 📥 KOMPONENTO PROPS GAUTI:', {
    botId,
    enabled,
    selectedInterval,
    primaryInterval,
    initialIndicators: initialIndicators ? {
      count: initialIndicators.length,
      types: initialIndicators.map(i => i.type || i.label)
    } : 'TUŠČIA_ARBA_NULL'
  });

  // =================================================================
  // BLOKAS 4: EFFECTS IR HANDLER'IAI
  // =================================================================

  // SUB-BLOKAS 4.1: useEffect botId pakeitimui
  useEffect(() => {
    console.log(`🔧 [BLOKAS 4.1 SecondaryTime.tsx] useEffect(): tikrinamas botId pakeitimas`);
    console.log(`🔧 [BLOKAS 4.1 SecondaryTime.tsx] useEffect(): ankstesnis botId: ${prevBotIdRef.current}, dabartinis botId: ${botId}`);
    
    if (prevBotIdRef.current !== botId) {
      console.log(`🔧 [BLOKAS 4.1 SecondaryTime.tsx] useEffect(): botId pasikeitė, atnaujinami indikatoriai`);
      setIndicators(normalizeIndicators(initialIndicators));
      setEditingIndicator(null);
      prevBotIdRef.current = botId;
      console.log(`🔧 [BLOKAS 4.1 SecondaryTime.tsx] useEffect(): indikatoriai atnaujinti, editingIndicator nustatytas į null`);
    } else {
      console.log(`🔧 [BLOKAS 4.1 SecondaryTime.tsx] useEffect(): botId nepakitęs, nieko nekeičiama`);
    }
  }, [initialIndicators, botId]);

  const currentIndicators = indicators;

  // SUB-BLOKAS 4.2: Indikatorių išsaugojimo funkcija
  async function saveIndicators(next: Indicator[]) {
    console.log(`🔧 [BLOKAS 4.2 SecondaryTime.tsx] saveIndicators(): pradedamas indikatorių išsaugojimas`);
    
    setIndicators(next);
    console.log(`🔧 [BLOKAS 4.2 SecondaryTime.tsx] saveIndicators(): state atnaujintas su ${next.length} indikatoriais`);
    
    // 🔥 SVARBU: Formatuojami indikatoriai DB/Lambda (pašalinamas id, paliekamas type + settings)
    const payload = next.map(({ id, ...rest }) => rest);

    console.log('🔥 [BLOKAS 4.2 SecondaryTime.tsx] saveIndicators(): Išsaugomi indikatoriai:', {
      originalCount: next.length,
      payloadCount: payload.length,
      payloadTypes: payload.map(p => p.type)
    });

    onIndicatorsChange?.(payload);
    console.log(`🔧 [BLOKAS 4.2 SecondaryTime.tsx] saveIndicators(): onIndicatorsChange iškviestas`);
    
    if (user?.id && botId && window.sendChange) {
      console.log(`🔧 [BLOKAS 4.2 SecondaryTime.tsx] saveIndicators(): siunčiamas pakeitimas į DB`);
      window.sendChange('secondaryTimeIndicators', payload);
    } else {
      console.log(`🔧 [BLOKAS 4.2 SecondaryTime.tsx] saveIndicators(): nepakanka duomenų DB išsaugojimui`);
    }
  }

  // SUB-BLOKAS 4.3: Įjungimo/išjungimo handler'is
  const handleToggleEnabled = () => {
    console.log(`🔧 [BLOKAS 4.3 SecondaryTime.tsx] handleToggleEnabled(): pradedamas įjungimo/išjungimo keitimas`);
    const newValue = !enabled;
    console.log(`🔧 [BLOKAS 4.3 SecondaryTime.tsx] handleToggleEnabled(): keičiama iš ${enabled} į ${newValue}`);

    // ✅ VISADA atnaujiname tėvų būseną (net jei botas dar neišsaugotas!)
    onEnabledChange(newValue);
    console.log(`🔧 [BLOKAS 4.3 SecondaryTime.tsx] handleToggleEnabled(): tėvų būsena atnaujinta`);

    // ✅ Automatinis išsaugojimas į DB tik jei botas jau egzistuoja
    if (user?.id && botId) {
      console.log(`🔧 [BLOKAS 4.3 SecondaryTime.tsx] handleToggleEnabled(): botas egzistuoja, siunčiama į DB`);
      if (window.sendChange) {
        window.sendChange('secondaryTimeEnabled', newValue);
      }
      toast.success(`Secondary time interval ${newValue ? 'enabled' : 'disabled'}`);
      console.log(`🔧 [BLOKAS 4.3 SecondaryTime.tsx] handleToggleEnabled(): toast pranešimas parodytas`);
    } else {
      // ✅ Nėra botId - tik atnaujinama formos būsena, klaidos nereikia
      console.log(`🔧 [BLOKAS 4.3 SecondaryTime.tsx] handleToggleEnabled(): nėra botId - formos būsena atnaujinta (bus išsaugota kuriant botą)`);
    }
  };

  // SUB-BLOKAS 4.4: Intervalo keitimo handler'is
  const handleIntervalChange = (newInterval: string) => {
    console.log(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): pradedamas intervalo keitimas`);
    console.log(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): keičiama iš ${selectedInterval} į ${newInterval}`);

    // Tikriname ar antrinis intervalas didesnis už pirminį
    const allowedIntervals = getAllowedSecondaryIntervals(primaryInterval);
    console.log(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): leidžiami intervalai: ${allowedIntervals.join(', ')}`);
    
    if (!allowedIntervals.includes(newInterval)) {
      console.error(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): klaida - intervalas ${newInterval} neleidžiamas`);
      toast.error('Secondary time interval must be larger than primary time interval');
      return;
    }

    onIntervalChange(newInterval); // Perduodame pakeitimą tėvui
    console.log(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): tėvų būsena atnaujinta`);

    if (user?.id && botId) {
      // Naudojame globalų sendChange jei prieinama
      console.log(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): botas egzistuoja, siunčiama į DB`);
      if (window.sendChange) {
        window.sendChange('secondaryTimeInterval', newInterval);
      }

      toast.success(`Secondary interval updated to ${newInterval}`);
      console.log(`🔧 [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): toast pranešimas parodytas`);
    } else {
      console.warn('⚠️ [BLOKAS 4.4 SecondaryTime.tsx] handleIntervalChange(): negalima atnaujinti intervalo - trūksta botId');
    }
  };

  // SUB-BLOKAS 4.5: Indikatorių helper funkcijos
  const hasConfigurableSettings = (indicatorType: string) => {
    console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] hasConfigurableSettings(): tikrinamas indikatorius ${indicatorType}`);
    const indicator = availableIndicators.find(i => i.value === indicatorType);
    const result = indicator ? Object.keys(indicator.defaultSettings).length > 0 : false;
    console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] hasConfigurableSettings(): ${indicatorType} turi konfigūruojamų nustatymų: ${result}`);
    return result;
  };

  const getIndicatorDisplayName = (indicator: Indicator) => {
    console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] getIndicatorDisplayName(): gaunamas rodymo vardas indikatoriui ${indicator.type}`);
    
    if (indicator.label) {
      console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] getIndicatorDisplayName(): naudojamas custom label: ${indicator.label}`);
      return indicator.label;
    }
    
    const foundIndicator = availableIndicators.find(i => i.value === indicator.type);
    const result = foundIndicator ? foundIndicator.label : indicator.type.toUpperCase();
    console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] getIndicatorDisplayName(): grąžinamas vardas: ${result}`);
    return result;
  };

  const formatSettingsSummary = (indicator: Indicator): string => {
    console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] formatSettingsSummary(): formatuojami nustatymai indikatoriui ${indicator.type}`);
    const s = indicator.settings || {};
    
    switch (indicator.type) {
      case 'rsi':
        console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] formatSettingsSummary(): RSI nustatymai: ${s.period ?? 14}/${s.overbought ?? 70}/${s.oversold ?? 30}`);
        return `(${s.period ?? 14}/${s.overbought ?? 70}/${s.oversold ?? 30})`;
      case 'macd': {
        const fast = s.fastPeriod ?? s.fast ?? 12;
        const slow = s.slowPeriod ?? s.slow ?? 26;
        const signal = s.signalPeriod ?? s.signal ?? 9;
        console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] formatSettingsSummary(): MACD nustatymai: ${fast}/${slow}/${signal}`);
        return `(${fast}/${slow}/${signal})`;
      }
      case 'bollinger':
        console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] formatSettingsSummary(): Bollinger nustatymai: ${s.period ?? 20}/${s.stdDev ?? 2}`);
        return `(${s.period ?? 20}/${s.stdDev ?? 2})`;
      case 'sma':
      case 'ema':
      case 'adx':
      case 'volume':
      case 'momentum':
        console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] formatSettingsSummary(): ${indicator.type} nustatymai: period=${s.period ?? 14}`);
        return `(${s.period ?? 14})`;
      default:
        console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] formatSettingsSummary(): ${indicator.type} neturi skaitinių nustatymų`);
        return '';
    }
  };

  console.log(`🔧 [BLOKAS 4.5 SecondaryTime.tsx] Indikatorių helper funkcijos apibrėžtos`);

  // Filtras leidžiamiems intervalams pagal pirminį intervalą
  // Leidžiami tik intervalai, kurie didesni už pirminį
  const availableIntervals = getAllowedSecondaryIntervals(primaryInterval);
  
  console.log('📊 [BLOKAS 4 SecondaryTime.tsx] Prieinami intervalai:', availableIntervals);
  console.log('📊 [BLOKAS 4 SecondaryTime.tsx] Pasirinktas intervalas:', selectedInterval);
  console.log('📊 [BLOKAS 4 SecondaryTime.tsx] Pirminis intervalas:', primaryInterval);

  // Filtras dropdown'ui, rodyti tik indikatorius kurie dar nepridėti
  const alreadyAddedTypes = currentIndicators.map((i: Indicator) => i.type);
  const availableForDropdown = availableIndicators.filter(
    indicator => !alreadyAddedTypes.includes(indicator.value)
  );

  console.log(`🔧 [BLOKAS 4 SecondaryTime.tsx] Dropdown: jau pridėtų tipų: ${alreadyAddedTypes.length}, prieinamų dropdown'ui: ${availableForDropdown.length}`);

  // SUB-BLOKAS 4.6: Indikatorių manipuliavimo funkcijos
  const handleAddIndicator = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const indicatorType = event.target.value;
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleAddIndicator(): pridedamas indikatorius ${indicatorType}`);

    if (!indicatorType) {
      console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleAddIndicator(): indikatorius nepasirinktas, nutraukiama`);
      return;
    }

    const indicator = availableIndicators.find(i => i.value === indicatorType);
    if (!indicator) {
      console.error(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleAddIndicator(): indikatorius ${indicatorType} nerastas`);
      return;
    }

    const exists = currentIndicators.some((ind: any) => ind.type === indicatorType);
    if (exists) {
      console.warn(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleAddIndicator(): indikatorius ${indicatorType} jau pridėtas`);
      toast.error(`${indicator.label} already added`);
      event.target.value = '';
      return;
    }

    const newIndicator: Indicator = {
      id: crypto.randomUUID(),
      type: indicatorType,
      settings: JSON.parse(JSON.stringify(indicator.defaultSettings)),
      label: indicator.label
    };

    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleAddIndicator(): sukurtas naujas indikatorius:`, newIndicator);
    const updatedIndicators = [...currentIndicators, newIndicator];
    await saveIndicators(updatedIndicators);
    toast.success(`Added ${indicator.label} to secondary time interval`);
    event.target.value = '';
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleAddIndicator(): indikatorius sėkmingai pridėtas`);
  };

  const handleRemoveIndicator = async (id: string) => {
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleRemoveIndicator(): šalinamas indikatorius ${id}`);

    const updatedIndicators = currentIndicators.filter((i: Indicator) => i.id !== id);
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleRemoveIndicator(): liko ${updatedIndicators.length} indikatorių`);

    await saveIndicators(updatedIndicators);
    toast.success('Secondary indicator removed');

    if (updatedIndicators.length === 0) {
      console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleRemoveIndicator(): visi indikatoriai pašalinti`);
      toast.info('All secondary indicators removed');
    }

    if (editingIndicator === id) {
      console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleRemoveIndicator(): šalinamas redaguojamas indikatorius, uždarytas redagavimas`);
      setEditingIndicator(null);
    }
  };

  const handleEditIndicator = (id: string) => {
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleEditIndicator(): redaguojamas indikatorius ${id}`);
    const newValue = editingIndicator === id ? null : id;
    setEditingIndicator(newValue);
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleEditIndicator(): editingIndicator nustatytas į ${newValue}`);
  };

  const handleUpdateSettings = async (id: string, settings: Record<string, any>) => {
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleUpdateSettings(): atnaujinami nustatymai indikatoriui ${id}`, settings);

    const processedSettings: Record<string, any> = {};
    for (const key in settings) {
      const value = settings[key];
      if (typeof value === 'string') {
        if (value === '') {
          processedSettings[key] = 0;
        } else if (!isNaN(Number(value))) {
          processedSettings[key] = Number(value);
        } else {
          processedSettings[key] = value;
        }
      } else {
        processedSettings[key] = value;
      }
    }

    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleUpdateSettings(): apdoroti nustatymai:`, processedSettings);

    const updatedIndicators = currentIndicators.map((i: Indicator) =>
      i.id === id ? { ...i, settings: processedSettings } : i
    );

    await saveIndicators(updatedIndicators);
    console.log(`🔧 [BLOKAS 4.6 SecondaryTime.tsx] handleUpdateSettings(): nustatymai atnaujinti`);
  };

  console.log(`🔧 [BLOKAS 4 SecondaryTime.tsx] Visi handlers'ai apibrėžti`);

  // =================================================================
  // BLOKAS 5: UI RENDERING
  // =================================================================

  console.log(`🔧 [BLOKAS 5 SecondaryTime.tsx] Pradedamas UI rendering'as`);
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Secondary Time Interval</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure secondary time interval for signal confirmation</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Secondary Time</span>
              <Tooltip
                content={
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-blue-400 mb-2">Secondary Time Interval Configuration</h5>
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium text-blue-400 mb-1">When Enabled:</p>
                          <ul className="space-y-1 text-gray-300">
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              Uses larger time interval for trend confirmation
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              Filters false signals from primary time interval
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              Provides broader market perspective
                            </li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-medium text-blue-400 mb-1">When Disabled:</p>
                          <ul className="space-y-1 text-gray-300">
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              Uses only primary time interval for signals
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              May be more sensitive to market noise
                            </li>
                            <li className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              Suitable for scalping strategies
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                      <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-blue-300 text-xs">
                        {enabled
                          ? "Using a larger time interval helps ensure trades align with broader market trends and reduces false signals."
                          : "Trading without secondary confirmation may increase risk but can be suitable for specific strategies."}
                      </p>
                    </div>
                  </div>
                }
                position="left"
              >
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                onClick={handleToggleEnabled}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  enabled ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    enabled ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          {enabled && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* SUB-BLOKAS 5.1: Antrinio intervalo pasirinkimas */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Secondary Time Interval
                  </label>
                  <Tooltip
                    content={
                      <div className="space-y-4">
                        <div>
                          <h5 className="font-medium text-blue-400 mb-2">Secondary Time Interval</h5>
                          <div className="space-y-3">
                            <div>
                              <p className="font-medium text-blue-400 mb-1">Purpose</p>
                              <ul className="space-y-1 text-gray-300">
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Confirms signals from primary time interval
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Shows broader market trends
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Reduces false signals
                                </li>
                              </ul>
                            </div>

                            <div>
                              <p className="font-medium text-blue-400 mb-1">Example Combinations</p>
                              <ul className="space-y-1 text-gray-300">
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Primary 5m → Secondary 15m or 30m
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Primary 15m → Secondary 1h or 4h
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Primary 1h → Secondary 4h or 1d
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                          <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className="text-blue-300 text-xs">
                            Choose a secondary interval that provides clear trend direction while maintaining reasonable signal frequency.
                          </p>
                        </div>
                      </div>
                    }
                    position="left"
                  >
                    <Info className="h-4 w-4 text-gray-400 cursor-help" />
                  </Tooltip>
                </div>

                <TimeInterval
                  intervals={availableIntervals}
                  selectedInterval={selectedInterval}
                  onIntervalChange={handleIntervalChange}
                />

                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  Signals from {primaryInterval} time interval will be confirmed by {selectedInterval} time interval
                </p>
              </div>

              {/* SUB-BLOKAS 5.2: Antrinių indikatorių valdymas */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Secondary Time Interval Indicators
                  </label>
                  <Tooltip
                    content={
                      <div className="space-y-4">
                        <div>
                          <h5 className="font-medium text-blue-400 mb-2">Secondary Time Interval Indicators</h5>
                          <div className="space-y-3">
                            <div>
                              <p className="font-medium text-blue-400 mb-1">Purpose</p>
                              <ul className="space-y-1 text-gray-300">
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Confirms primary time interval signals
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Identifies broader market trends
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Filters market noise
                                </li>
                          </ul>
                            </div>

                            <div>
                              <p className="font-medium text-blue-400 mb-1">Signal Confirmation</p>
                              <ul className="space-y-1 text-gray-300">
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Primary and secondary must align
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Use 2-3 additional indicators
                                </li>
                                <li className="flex items-center gap-2">
                                  <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Avoid redundant indicators
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                          <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <p className="text-blue-300 text-xs">
                            Choose indicators that complement your primary time interval strategy.
                          </p>
                        </div>
                      </div>
                    }
                    position="left"
                  >
                    <Info className="h-4 w-4 text-gray-400 cursor-help" />
                  </Tooltip>
                </div>
            
            <div className="space-y-4">
                  {Array.isArray(currentIndicators) && currentIndicators.map((indicator: Indicator, index: number) => {
                    console.log(`🔧 [BLOKAS 5.2 SecondaryTime.tsx] Rendering indikatorius ${index}: ${indicator.type}`);
                    const isExpanded = editingIndicator === indicator.id;
                    const settingsSummary = formatSettingsSummary(indicator);
                    const hasSettings = hasConfigurableSettings(indicator.type);

                    return (
                      <motion.div
                        key={indicator.id || `indicator-${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {getIndicatorDisplayName(indicator)}
                            </span>
                            {settingsSummary && !isExpanded && (
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {settingsSummary}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {hasSettings && (
                              <button
                                onClick={() => handleEditIndicator(indicator.id)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-blue-500" />
                                ) : (
                                  <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveIndicator(indicator.id)}
                              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4">
                            {renderIndicatorSettings(indicator, handleUpdateSettings)}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                 
                  {(!currentIndicators || currentIndicators.length === 0) && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <SlidersHorizontal className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No secondary indicators configured</p>
                      <p className="text-xs">Add indicators from the dropdown below</p>
                    </div>
                  )}
                </div>

                {/* SUB-BLOKAS 5.3: Indikatoriaus pridėjimo dropdown */}
                <div className="relative">
                  <select
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    value=""
                    onChange={handleAddIndicator}
                  >
                    <option value="" disabled>Add Indicator</option>
                    {availableForDropdown.map((indicator) => (
                      <option key={indicator.value} value={indicator.value}>
                        {indicator.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <SlidersHorizontal className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
    </motion.div>
  );
};

console.log(`🔧 [BLOKAS 5 SecondaryTime.tsx] UI rendering'as apibrėžtas`);

// =================================================================
// BLOKAS 6: RENDER INDICATOR SETTINGS FUNKCIJA
// =================================================================

function renderIndicatorSettings(indicator: Indicator, handleUpdateSettings: (id: string, settings: Record<string, any>) => void) {
  console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): pradedamas indikatoriaus ${indicator.type} nustatymų rendering'as`);
  
  const indicatorConfig = availableIndicators.find(i => i.value === indicator.type);
  if (!indicatorConfig) {
    console.warn(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): indikatoriaus ${indicator.type} konfigūracija nerasta`);
    return null;
  }

  if (Object.keys(indicatorConfig.defaultSettings).length === 0) {
    console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): indikatorius ${indicator.type} neturi konfigūruojamų skaitinių nustatymų`);
    return (
      <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-600 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          This indicator has no configurable numeric settings.
        </p>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white no-spinner";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";
  const s = indicator.settings || {};

  console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): indikatorius ${indicator.type} turi nustatymus:`, s);

  switch (indicator.type) {
    case 'rsi':
      console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): generuojami RSI nustatymų input'ai`);
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Period</label>
              <input
                type="number"
                value={s.period ?? 14}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  period: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Overbought</label>
              <input
                type="number"
                value={s.overbought ?? 70}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  overbought: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Oversold</label>
              <input
                type="number"
                value={s.oversold ?? 30}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  oversold: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      );

    case 'macd': {
      const fast = s.fastPeriod ?? s.fast ?? 12;
      const slow = s.slowPeriod ?? s.slow ?? 26;
      const signal = s.signalPeriod ?? s.signal ?? 9;
      console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): generuojami MACD nustatymų input'ai: ${fast}/${slow}/${signal}`);
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Fast Period</label>
              <input
                type="number"
                value={fast}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  fastPeriod: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Slow Period</label>
              <input
                type="number"
                value={slow}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  slowPeriod: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Signal Period</label>
              <input
                type="number"
                value={signal}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  signalPeriod: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      );
    }

    case 'bollinger':
      console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): generuojami Bollinger nustatymų input'ai`);
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Period</label>
              <input
                type="number"
                value={s.period ?? 20}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  period: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Standard Deviation</label>
              <input
                type="number"
                value={s.stdDev ?? 2}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  stdDev: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                className={inputClass}
                step="0.1"
              />
            </div>
          </div>
        </div>
      );

    case 'adx':
    case 'sma':
    case 'ema':
    case 'volume':
    case 'momentum':
      console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): generuojamas ${indicator.type} nustatymų input'as`);
      return (
        <div className="space-y-4">
          <div>
            <label className={labelClass}>Period</label>
            <input
              type="number"
              value={s.period ?? 14}
              onChange={(e) => handleUpdateSettings(indicator.id, {
                ...s,
                period: e.target.value === '' ? 0 : Number(e.target.value)
              })}
              className={inputClass}
            />
          </div>
        </div>
      );

    default:
      console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings(): indikatoriui ${indicator.type} nėra specialių nustatymų`);
      return null;
  }
}

console.log(`🔧 [BLOKAS 6 SecondaryTime.tsx] renderIndicatorSettings() funkcija apibrėžta`);

export default SecondaryTime;

console.log(`🔧 [VISAS FAILAS SecondaryTime.tsx] Kodas sėkmingai sunumeruotas ir apdorotas`);